import { LayoutRight } from '@untitledui/icons';
import { type SyntheticEvent, useCallback, useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { RadioCard, RadioGroup } from '@/lib/base/radio-buttons/radio-buttons';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import type { EntityTypeName } from '@/lib/crm/core/types';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { CrmObject } from '@/types/crm';

import { CardBody } from './MergeStep1PickCanonical.cards';
import { useMergeWizard } from './MergeWizardContext';

export interface MergeStep1PickCanonicalProps {
  leftObject: CrmObject | undefined;
  rightObject: CrmObject | undefined;
  isLoading: boolean;
  entityTypeName: EntityTypeName;
}

interface MergeRecordCardProps {
  obj: CrmObject | undefined;
  side: 'left' | 'right';
  schema: PropertySchemaMap;
  entityTypeName: EntityTypeName;
  orgId: number;
  assocAccessMap: ReadonlyMap<string, string>;
  assocLabelMap: ReadonlyMap<string, string>;
  stageAssocTypeNames: ReadonlySet<string>;
  openSlideout?: (id: string) => void;
}

function MergeRecordCard({
  obj,
  side,
  schema,
  entityTypeName,
  orgId,
  assocAccessMap,
  assocLabelMap,
  stageAssocTypeNames,
  openSlideout,
}: MergeRecordCardProps) {
  const name = displayNameFor(obj);

  const stopBubble = useCallback((e: SyntheticEvent) => {
    e.stopPropagation();
  }, []);
  const handleViewDetails = useCallback(() => {
    if (obj) {
      openSlideout?.(obj.entity_id);
    }
  }, [obj, openSlideout]);

  if (!obj) {
    return (
      <div
        data-testid={`merge-card-${side}`}
        className='flex cursor-not-allowed flex-col gap-3 rounded-lg border border-[var(--color-border-secondary)] p-4 opacity-50'
      >
        <CardBody
          obj={obj}
          name={name}
          schema={schema}
          entityTypeName={entityTypeName}
          orgId={orgId}
          assocAccessMap={assocAccessMap}
          assocLabelMap={assocLabelMap}
          stageAssocTypeNames={stageAssocTypeNames}
        />
      </div>
    );
  }

  return (
    <RadioCard value={obj.entity_id} data-testid={`merge-card-${side}`}>
      <CardBody
        obj={obj}
        name={name}
        schema={schema}
        entityTypeName={entityTypeName}
        orgId={orgId}
        assocAccessMap={assocAccessMap}
        assocLabelMap={assocLabelMap}
        stageAssocTypeNames={stageAssocTypeNames}
        headerSuffix={
          <span
            className='shrink-0'
            onPointerDown={stopBubble}
            onClick={stopBubble}
          >
            <Tooltip title='View details' placement='top'>
              <TooltipTrigger
                onPress={handleViewDetails}
                className='cursor-pointer rounded-lg p-1.5 transition-colors hover:bg-[var(--color-bg-tertiary)]'
                aria-label={`View details for ${name}`}
                data-testid={`merge-view-details-${side}`}
              >
                <LayoutRight
                  className='size-4 text-fg-quaternary'
                  aria-hidden='true'
                />
              </TooltipTrigger>
            </Tooltip>
          </span>
        }
      />
    </RadioCard>
  );
}

function displayNameFor(obj: CrmObject | undefined): string {
  if (!obj) {
    return 'Unknown';
  }
  // Generic fallback — never a raw/truncated entity id.
  return getDisplayName(obj, 'Unnamed record');
}

export function MergeStep1PickCanonical({
  leftObject,
  rightObject,
  isLoading,
  entityTypeName,
}: MergeStep1PickCanonicalProps) {
  const { canonicalId, setCanonicalId } = useMergeWizard();
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id ?? 0;
  const { schema } = usePropertySchema(orgId, entityTypeName);
  const openSlideout = useOpenSlideoutOptional();

  // Association metadata for the type-aware association rows. Access is
  // FAIL-CLOSED in getMergeCardRows: while these are loading the maps are
  // empty, so association rows are simply absent (never briefly leaking
  // `internal` types) — mirrors the gating in overview-section.
  //
  // `stageAssociationTypes` is keyed by association type name (target kind
  // `stage`), the same key space as `MergeCardRow.key` — so an association
  // row can resolve to the stage-specific label. (The stage ENTITY type set
  // from usePipelineStages is a different key space and never matches a row
  // key, which left every stage row rendering as a plain entity badge.)
  const { associationTypes, stageAssociationTypes } = useAssociationTypes(
    orgId > 0 ? orgId : undefined
  );
  const assocAccessMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const assocType of associationTypes) {
      map.set(assocType.association_type_name, assocType.value_read_access);
    }
    return map;
  }, [associationTypes]);
  const assocLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const assocType of associationTypes) {
      map.set(assocType.association_type_name, assocType.label);
    }
    return map;
  }, [associationTypes]);

  const handleChange = useCallback(
    (value: string) => {
      setCanonicalId(value);
    },
    [setCanonicalId]
  );

  if (isLoading && leftObject === undefined && rightObject === undefined) {
    return (
      <div
        className='grid grid-cols-1 gap-4 md:grid-cols-2'
        data-testid='merge-step1-loading'
      >
        <Skeleton className='h-48 w-full rounded-lg' />
        <Skeleton className='h-48 w-full rounded-lg' />
      </div>
    );
  }

  return (
    <>
      <p className='mb-4 text-body-small text-[var(--color-text-secondary)]'>
        Select the record to keep. The other record will be merged into it.
      </p>
      <RadioGroup
        aria-label='Choose canonical record'
        value={canonicalId ?? ''}
        onChange={handleChange}
        className='grid grid-cols-1 gap-4 md:grid-cols-2'
      >
        <MergeRecordCard
          obj={leftObject}
          side='left'
          schema={schema}
          entityTypeName={entityTypeName}
          orgId={orgId}
          assocAccessMap={assocAccessMap}
          assocLabelMap={assocLabelMap}
          stageAssocTypeNames={stageAssociationTypes}
          openSlideout={openSlideout}
        />
        <MergeRecordCard
          obj={rightObject}
          side='right'
          schema={schema}
          entityTypeName={entityTypeName}
          orgId={orgId}
          assocAccessMap={assocAccessMap}
          assocLabelMap={assocLabelMap}
          stageAssocTypeNames={stageAssociationTypes}
          openSlideout={openSlideout}
        />
      </RadioGroup>
    </>
  );
}
